import { Controller } from "@hotwired/stimulus"

/**
 * Report Welders Controller
 * Manages dynamic add/remove of welders to a report
 */
export default class extends Controller {
  static targets = ["container", "welderSelect", "tableBody", "hiddenFields"]
  static outlets = ["autosave"]

  connect() {
    console.log('[report-welders] Controller connected')
    this.weldersData = new Map() // Store welder data by ID
    this.loadExistingWelders()
  }

  /**
   * Load existing welders from the table
   */
  loadExistingWelders() {
    if (!this.hasTableBodyTarget) return

    const rows = this.tableBodyTarget.querySelectorAll('tr[data-welder-id]')
    rows.forEach(row => {
      const welderId = row.dataset.welderId
      const cells = row.querySelectorAll('td')

      this.weldersData.set(welderId, {
        id: welderId,
        name: cells[0]?.textContent?.trim() || '',
        stampMark: cells[1]?.textContent?.trim() || '',
        idNumber: cells[2]?.textContent?.trim() || '',
        certification: cells[3]?.textContent?.trim() || ''
      })
    })

    console.log('[report-welders] Loaded welders:', this.weldersData.size)
  }

  /**
   * Add a welder to the report
   */
  async addWelder(event) {
    event.preventDefault()

    const welderId = this.welderSelectTarget.value

    if (!welderId) {
      alert('Please select a welder first')
      return
    }

    // Check if welder is already added
    if (this.weldersData.has(welderId)) {
      alert('This welder is already added to the report')
      this.welderSelectTarget.value = ''
      return
    }

    // Fetch welder data from server
    try {
      const response = await fetch(`/welder_certifications/welders/${welderId}.json`)
      if (!response.ok) throw new Error('Failed to fetch welder data')

      const welderData = await response.json()

      // Add to our data map
      this.weldersData.set(welderId, {
        id: welderId,
        name: welderData.full_name || `${welderData.first_name} ${welderData.last_name}`,
        stampMark: welderData.stamp_mark || '',
        idNumber: welderData.id_number || '',
        certification: welderData.certificates?.map(c => c.certificate_number).join(', ') || 'N/A'
      })

      // Update UI
      this.renderWelders()

      // Reset select
      this.welderSelectTarget.value = ''

      // Trigger select2 change if it exists
      if (window.jQuery && window.jQuery(this.welderSelectTarget).data('select2')) {
        window.jQuery(this.welderSelectTarget).val('').trigger('change')
      }

      console.log('[report-welders] Welder added:', welderId)

      // Trigger autosave
      await this.triggerAutosave()
    } catch (error) {
      console.error('[report-welders] Error adding welder:', error)
      alert('Failed to add welder. Please try again.')
    }
  }

  /**
   * Remove a welder from the report
   */
  async removeWelder(event) {
    event.preventDefault()

    const button = event.currentTarget
    const welderId = button.dataset.welderId

    if (!welderId) return

    // Remove from data map
    this.weldersData.delete(welderId)

    // Update UI
    this.renderWelders()

    console.log('[report-welders] Welder removed:', welderId)

    // Trigger autosave
    await this.triggerAutosave()
  }

  /**
   * Render the welders table
   */
  renderWelders() {
    // Check if we have any welders
    if (this.weldersData.size === 0) {
      this.containerTarget.innerHTML = `
        <div class="text-center text-muted py-3">
          <i class="fa fa-users mr-2"></i>
          No welders added yet
        </div>
      `
      this.updateHiddenFields()
      return
    }

    // Create table
    let tableHTML = `
      <div class="table-responsive">
        <table class="table table-sm table-hover">
          <thead>
            <tr>
              <th>Welder Name</th>
              <th>Stamp Mark</th>
              <th>ID Number</th>
              <th>Certification</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody data-report-welders-target="tableBody">
    `

    // Add rows for each welder
    this.weldersData.forEach((welder, welderId) => {
      tableHTML += `
        <tr data-welder-id="${welderId}">
          <td>${this.escapeHtml(welder.name)}</td>
          <td>${this.escapeHtml(welder.stampMark)}</td>
          <td>${this.escapeHtml(welder.idNumber)}</td>
          <td>${welder.certification === 'N/A' ? '<span class="text-muted">N/A</span>' : this.escapeHtml(welder.certification)}</td>
          <td>
            <button class="btn btn-sm btn-link text-danger p-0" type="button"
                    data-action="click->report-welders#removeWelder"
                    data-welder-id="${welderId}"
                    title="Remove">
              <i class="fa fa-trash"></i>
            </button>
          </td>
        </tr>
      `
    })

    tableHTML += `
          </tbody>
        </table>
      </div>
    `

    this.containerTarget.innerHTML = tableHTML

    // Update hidden fields
    this.updateHiddenFields()
  }

  /**
   * Update hidden fields with current welder IDs
   */
  updateHiddenFields() {
    if (!this.hasHiddenFieldsTarget) return

    let hiddenHTML = ''
    this.weldersData.forEach((welder, welderId) => {
      hiddenHTML += `<input type="hidden" name="report[welder_ids][]" value="${welderId}" />\n`
    })

    this.hiddenFieldsTarget.innerHTML = hiddenHTML
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  /**
   * Trigger autosave via outlet
   */
  async triggerAutosave() {
    console.log('╔═══════════════════════════════════════════╗')
    console.log('║      WELDER CHANGE - TRIGGER SAVE         ║')
    console.log('╚═══════════════════════════════════════════╝')
    console.log('[report-welders] Has autosave outlet:', this.hasAutosaveOutlet)

    if (this.hasAutosaveOutlet) {
      console.log('[report-welders] ✓ Autosave outlet found, calling saveNow()...')
      console.log('[report-welders] Outlet:', this.autosaveOutlet)
      await this.autosaveOutlet.saveNow()
      console.log('[report-welders] ✅ Welder save complete')
    } else {
      console.error('[report-welders] ❌ NO AUTOSAVE OUTLET AVAILABLE!')
      console.log('[report-welders] Available outlets:', Object.keys(this).filter(k => k.includes('Outlet')))
    }
  }
}
